/* File: src/components/game/hooks/useGameResponses.js */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useGame } from '../../../contexts/GameContext';

// Constants for feedback timing
const ACKNOWLEDGMENT_DURATION = 150; // ms - Short duration for acknowledgment flash
const CORRECTNESS_FEEDBACK_DURATION = 300; // ms
const MISSED_MATCH_DELAY = 50; // ms, short delay before showing missed feedback

/**
 * Custom hook to manage user responses during gameplay.
 * - Tracks visual and audio responses per trial.
 * - Provides feedback state ('correct', 'incorrect', 'missed', 'acknowledged').
 * - Checks for missed matches automatically when trials advance (only if instant feedback is enabled).
 * - Uses GameContext for logging, feedback settings, match checking, and state.
 */
const useGameResponses = () => {
    // --- Get ALL needed context values via useGame ---
    const {
        // Actions
        registerResponse,       // Function to record raw response in context
        logReplayEvent,         // Function to log events for replay
        playFeedbackSound,      // Function to play feedback sounds

        // Getters
        isMatch,                // Function to check if a trial is a match
        getInstantFeedbackEnabled, // Function to check settings

        // State
        sequence,               // Current game sequence array
        currentTrial,           // Current trial index (0-based)

        // Refs
        latestGameStateRef      // Ref holding the most current game state
    } = useGame();

    // --- Local State for Feedback UI ---
    const [positionFeedback, setPositionFeedback] = useState(null); // 'correct', 'incorrect', 'missed', 'acknowledged', null
    const [audioFeedback, setAudioFeedback] = useState(null);       // 'correct', 'incorrect', 'missed', 'acknowledged', null
    const [missedMatchTrial, setMissedMatchTrial] = useState(null); // Stores the index of the missed trial

    // State to track which trials user has already responded to
    const [visualResponseTrials, setVisualResponseTrials] = useState([]);
    const [audioResponseTrials, setAudioResponseTrials] = useState([]);

    // --- Refs ---
    // Refs to prevent repeated 'missed' feedback flashes for the *same* trial index
    const lastMissedVisualTrialRef = useRef(null);
    const lastMissedAudioTrialRef = useRef(null);

    // Refs for the INNER timeouts (clearing feedback display after it shows)
    const innerFeedbackTimeouts = useRef({ visual: null, audio: null });
    // Refs for the OUTER timeouts (delaying the initiation of missed feedback)
    const outerMissedCheckTimeouts = useRef({ visual: null, audio: null });

    // --- Helper: Force Clear ALL Pending Timeouts ---
    // Used ONLY for major resets (new game)
    const cancelAllPendingFeedbackTimeouts = useCallback(() => {
        if (innerFeedbackTimeouts.current.visual) clearTimeout(innerFeedbackTimeouts.current.visual);
        if (innerFeedbackTimeouts.current.audio) clearTimeout(innerFeedbackTimeouts.current.audio);
        if (outerMissedCheckTimeouts.current.visual) clearTimeout(outerMissedCheckTimeouts.current.visual);
        if (outerMissedCheckTimeouts.current.audio) clearTimeout(outerMissedCheckTimeouts.current.audio);
        innerFeedbackTimeouts.current = { visual: null, audio: null }; // Reset refs
        outerMissedCheckTimeouts.current = { visual: null, audio: null }; // Reset refs
    }, []); // No dependencies, relies only on refs

    // --- Helper: Cancel ONLY OUTER Pending Timeouts ---
    // Used by effect cleanup to prevent stale miss checks, but allow inner clears to finish
    const cancelOuterPendingTimeouts = useCallback(() => {
        if (outerMissedCheckTimeouts.current.visual) {
            clearTimeout(outerMissedCheckTimeouts.current.visual);
            outerMissedCheckTimeouts.current.visual = null;
        }
        if (outerMissedCheckTimeouts.current.audio) {
            clearTimeout(outerMissedCheckTimeouts.current.audio);
            outerMissedCheckTimeouts.current.audio = null;
        }
    }, []); // No dependencies

    // --- Handler: Position (Visual) Response ---
    const handlePositionResponse = useCallback((trialIndex) => {
        // Check game state and if already responded
        if (latestGameStateRef.current !== 'playing' || visualResponseTrials.includes(trialIndex)) return;
        // Check for required functions from context
        if (typeof registerResponse !== 'function' || typeof logReplayEvent !== 'function' || typeof isMatch !== 'function' || typeof getInstantFeedbackEnabled !== 'function' || typeof playFeedbackSound !== 'function') {
             console.error("Missing context functions in useGameResponses for handlePositionResponse!"); return;
        }

        logReplayEvent({ type: 'responsePosition', trialIndex });
        setVisualResponseTrials(prev => [...prev, trialIndex]); // Track response
        registerResponse('visual', trialIndex); // Register in context
        const instantFeedbackEnabled = getInstantFeedbackEnabled(); // Check setting

        // *** Clear specific pending VISUAL timeouts (inner & outer) before setting new feedback ***
        if (innerFeedbackTimeouts.current.visual) {
            clearTimeout(innerFeedbackTimeouts.current.visual);
            innerFeedbackTimeouts.current.visual = null;
        }
        if (outerMissedCheckTimeouts.current.visual) {
            clearTimeout(outerMissedCheckTimeouts.current.visual);
            outerMissedCheckTimeouts.current.visual = null;
        }
        setMissedMatchTrial(null); // Still clear missed highlight

        // Provide feedback
        if (instantFeedbackEnabled) {
            const isCorrect = isMatch('visual', trialIndex);
            const feedbackType = isCorrect ? 'correct' : 'incorrect';
            setPositionFeedback(feedbackType); // Set feedback state directly
            playFeedbackSound(feedbackType);   // Play sound
            logReplayEvent({ type: 'feedbackVisual', trialIndex, feedbackType }); // Log feedback event
            // Schedule the clearing of THIS specific feedback display
            innerFeedbackTimeouts.current.visual = setTimeout(() => {
                setPositionFeedback(null); // Clear UI feedback
                innerFeedbackTimeouts.current.visual = null; // Clear the timeout ref
            }, CORRECTNESS_FEEDBACK_DURATION);
        } else {
            // ** NEW: Provide simple acknowledgment feedback **
            setPositionFeedback('acknowledged'); // Set feedback state directly
            logReplayEvent({ type: 'feedbackVisual', trialIndex, feedbackType: 'acknowledged', disabled: true });
            // Schedule the clearing of THIS specific acknowledgment display
            innerFeedbackTimeouts.current.visual = setTimeout(() => {
                setPositionFeedback(null); // Clear UI feedback
                innerFeedbackTimeouts.current.visual = null; // Clear the timeout ref
            }, ACKNOWLEDGMENT_DURATION); // Use shorter duration
        }
    }, [visualResponseTrials, registerResponse, isMatch, logReplayEvent, getInstantFeedbackEnabled, playFeedbackSound, latestGameStateRef]); // Keep latestGameStateRef dep

    // --- Handler: Audio Response ---
    const handleAudioResponse = useCallback((trialIndex) => {
        // Check game state and if already responded
        if (latestGameStateRef.current !== 'playing' || audioResponseTrials.includes(trialIndex)) return;
        // Check for required functions from context
        if (typeof registerResponse !== 'function' || typeof logReplayEvent !== 'function' || typeof isMatch !== 'function' || typeof getInstantFeedbackEnabled !== 'function' || typeof playFeedbackSound !== 'function') {
             console.error("Missing context functions in useGameResponses for handleAudioResponse!"); return;
        }

        logReplayEvent({ type: 'responseAudio', trialIndex });
        setAudioResponseTrials(prev => [...prev, trialIndex]); // Track response
        registerResponse('audio', trialIndex); // Register in context
        const instantFeedbackEnabled = getInstantFeedbackEnabled(); // Check setting

        // *** Clear specific pending AUDIO timeouts (inner & outer) before setting new feedback ***
        if (innerFeedbackTimeouts.current.audio) {
            clearTimeout(innerFeedbackTimeouts.current.audio);
            innerFeedbackTimeouts.current.audio = null;
        }
        if (outerMissedCheckTimeouts.current.audio) {
            clearTimeout(outerMissedCheckTimeouts.current.audio);
            outerMissedCheckTimeouts.current.audio = null;
        }

        // Provide feedback
        if (instantFeedbackEnabled) {
            const isCorrect = isMatch('audio', trialIndex);
            const feedbackType = isCorrect ? 'correct' : 'incorrect';
            setAudioFeedback(feedbackType); // Set feedback state directly
            playFeedbackSound(feedbackType); // Play sound
            logReplayEvent({ type: 'feedbackAudio', trialIndex, feedbackType }); // Log feedback event
            // Schedule the clearing of THIS specific feedback display
            innerFeedbackTimeouts.current.audio = setTimeout(() => {
                setAudioFeedback(null); // Clear UI feedback
                innerFeedbackTimeouts.current.audio = null; // Clear the timeout ref
            }, CORRECTNESS_FEEDBACK_DURATION);
        } else {
            // ** NEW: Provide simple acknowledgment feedback **
            setAudioFeedback('acknowledged'); // Set feedback state directly
            logReplayEvent({ type: 'feedbackAudio', trialIndex, feedbackType: 'acknowledged', disabled: true });
            // Schedule the clearing of THIS specific acknowledgment display
            innerFeedbackTimeouts.current.audio = setTimeout(() => {
                setAudioFeedback(null); // Clear UI feedback
                innerFeedbackTimeouts.current.audio = null; // Clear the timeout ref
            }, ACKNOWLEDGMENT_DURATION); // Use shorter duration
        }
    }, [audioResponseTrials, registerResponse, isMatch, logReplayEvent, getInstantFeedbackEnabled, playFeedbackSound, latestGameStateRef]); // Keep latestGameStateRef dep

    // --- Effect to Check for Missed Matches (Only runs if instant feedback is enabled) ---
    useEffect(() => {
        // Check for required functions first
        if (typeof isMatch !== 'function' || typeof getInstantFeedbackEnabled !== 'function' || typeof logReplayEvent !== 'function' || typeof playFeedbackSound !== 'function') {
            return;
        }

        const instantFeedbackEnabled = getInstantFeedbackEnabled();
        // *** Only run missed match checks if instant feedback IS enabled ***
        if (!instantFeedbackEnabled || latestGameStateRef.current !== 'playing') {
            return; // Skip if feedback is off or not playing
        }

        const prevTrial = currentTrial - 1; // Check the trial that just finished
        // Check if previous trial index is valid
        if (prevTrial < 0 || !Array.isArray(sequence) || prevTrial >= sequence.length) {
            return;
        }

        // --- Check Missed Visual Match ---
        const visualWasMatch = isMatch('visual', prevTrial);
        if (visualWasMatch && !visualResponseTrials.includes(prevTrial)) {
            // Only trigger miss feedback if we haven't already flagged this trial as missed
            if (lastMissedVisualTrialRef.current !== prevTrial) {
                lastMissedVisualTrialRef.current = prevTrial; // Mark as missed for this check cycle
                logReplayEvent({ type: 'feedbackVisual', trialIndex: prevTrial, feedbackType: 'missed' });

                // Clear specific pending VISUAL timeouts (inner & outer) before scheduling new outer
                if (outerMissedCheckTimeouts.current.visual) clearTimeout(outerMissedCheckTimeouts.current.visual);
                if (innerFeedbackTimeouts.current.visual) clearTimeout(innerFeedbackTimeouts.current.visual);
                setPositionFeedback(null); // Ensure visual feedback is null before scheduling
                setMissedMatchTrial(null);

                // Schedule the delayed check for showing miss feedback
                outerMissedCheckTimeouts.current.visual = setTimeout(() => {
                    outerMissedCheckTimeouts.current.visual = null; // Clear self immediately
                    // Check game state AGAIN inside the timeout using the ref
                    if (latestGameStateRef.current === 'playing') {
                        setPositionFeedback('incorrect'); // Show 'incorrect' style feedback for miss
                        setMissedMatchTrial(prevTrial);   // Highlight the grid square
                        playFeedbackSound('incorrect'); // Play sound for miss
                        // Schedule the clearing of *this* specific miss feedback display
                        innerFeedbackTimeouts.current.visual = setTimeout(() => {
                            // Check state again before clearing visual feedback
                            if (latestGameStateRef.current === 'playing') {
                                setPositionFeedback(null);
                                setMissedMatchTrial(null); // Clear grid highlight
                            }
                            innerFeedbackTimeouts.current.visual = null; // Clear the timeout ref regardless
                        }, CORRECTNESS_FEEDBACK_DURATION);
                    }
                }, MISSED_MATCH_DELAY);
            }
        } else {
            // Reset the guard if the previous trial was NOT a missed visual match
            if (lastMissedVisualTrialRef.current === prevTrial) {
                lastMissedVisualTrialRef.current = null;
            }
        }

        // --- Check Missed Audio Match ---
        const audioWasMatch = isMatch('audio', prevTrial);
        if (audioWasMatch && !audioResponseTrials.includes(prevTrial)) {
             // Only trigger miss feedback if we haven't already flagged this trial as missed
            if (lastMissedAudioTrialRef.current !== prevTrial) {
                lastMissedAudioTrialRef.current = prevTrial; // Mark as missed for this check cycle
                logReplayEvent({ type: 'feedbackAudio', trialIndex: prevTrial, feedbackType: 'missed' });

                // Clear specific pending AUDIO timeouts (inner & outer) before scheduling new outer
                if (outerMissedCheckTimeouts.current.audio) clearTimeout(outerMissedCheckTimeouts.current.audio);
                if (innerFeedbackTimeouts.current.audio) clearTimeout(innerFeedbackTimeouts.current.audio);
                setAudioFeedback(null); // Ensure audio feedback is null before scheduling

                // Schedule the delayed check for showing miss feedback
                outerMissedCheckTimeouts.current.audio = setTimeout(() => {
                    outerMissedCheckTimeouts.current.audio = null; // Clear self immediately
                    // Check game state AGAIN inside the timeout using the ref
                    if (latestGameStateRef.current === 'playing') {
                        setAudioFeedback('incorrect'); // Show 'incorrect' style feedback for miss
                        playFeedbackSound('incorrect'); // Play sound for miss
                        // Schedule the clearing of *this* specific miss feedback display
                        innerFeedbackTimeouts.current.audio = setTimeout(() => {
                            // Check state again before clearing audio feedback
                            if (latestGameStateRef.current === 'playing') {
                                setAudioFeedback(null);
                            }
                            innerFeedbackTimeouts.current.audio = null; // Clear the timeout ref regardless
                        }, CORRECTNESS_FEEDBACK_DURATION);
                    }
                }, MISSED_MATCH_DELAY);
            }
        } else {
            // Reset the guard if the previous trial was NOT a missed audio match
            if (lastMissedAudioTrialRef.current === prevTrial) {
                lastMissedAudioTrialRef.current = null;
            }
        }

        // --- Effect Cleanup ---
        // Cancel potentially pending *outer* checks when dependencies change before the next run.
        return () => {
            cancelOuterPendingTimeouts(); // Use the specific outer canceller
        };

    }, [
        // Dependencies that trigger the check
        currentTrial,
        sequence, // Need sequence to check content of prevTrial
        // Context functions/state needed inside
        isMatch,
        visualResponseTrials, // Needed to check if response was made
        audioResponseTrials,  // Needed to check if response was made
        logReplayEvent,
        getInstantFeedbackEnabled, // Critical dependency for the effect's conditional execution
        playFeedbackSound,
        latestGameStateRef, // Ref is stable, but used directly
        // Include the specific outer canceller function as it's used in cleanup
        cancelOuterPendingTimeouts
    ]);

    // --- Reset State Function ---
    // Called externally (e.g., by useGameFlow) when starting a new game/retry.
    const resetResponses = useCallback(() => {
        // Force clear ALL known pending timeouts FIRST (inner and outer)
        cancelAllPendingFeedbackTimeouts(); // Use the full canceller here for complete reset

        // Reset UI feedback states
        setPositionFeedback(null);
        setAudioFeedback(null);
        setMissedMatchTrial(null);

        // Reset response tracking arrays
        setVisualResponseTrials([]);
        setAudioResponseTrials([]);

        // Reset missed match guard refs
        lastMissedVisualTrialRef.current = null;
        lastMissedAudioTrialRef.current = null;

        console.log("[useGameResponses] Feedback and response state reset.");
    }, [cancelAllPendingFeedbackTimeouts]); // Dependency on the full canceller

    // --- Return Hook Interface ---
    // Expose state values and handler functions needed by the consuming component (GameScreen).
    return {
        // State values for UI feedback
        positionFeedback,
        audioFeedback,
        missedMatchTrial,       // Index for PositionGrid highlight
        visualResponseTrials,   // Array of visual response trials (useful for grid state)
        audioResponseTrials,    // Array of audio response trials

        // Handler functions to be called by UI controls or keybindings
        handlePositionResponse,
        handleAudioResponse,

        // Function to reset state (called by useGameFlow/GameScreen)
        resetResponses,
    };
};

export default useGameResponses;